import React, { useEffect, useState } from 'react';
import { Typography, Breadcrumb, Card, Table, Button, Space, message } from 'antd';
import api from '../../../utils/api';
import { Select } from 'antd';

const { Title, Paragraph } = Typography;

// Feature matrix (rows). Each feature is edited per-role via Select with string values.
const FEATURES = [
  { key: 'user_management', title: 'User Management' },
  { key: 'branding_controls', title: 'Branding Controls' },
  { key: 'pricing_controls', title: 'Pricing Controls' },
  { key: 'data_access_scope', title: 'Data Access Scope' },
  { key: 'audience_creation_tools', title: 'Audience Creation Tools' },
  { key: 'advanced_data', title: 'NeighborhoodLens (Advanced Data)' },
  { key: 'analytics_reporting', title: 'Analytics & Reporting' },
  { key: 'billing_invoicing', title: 'Billing & Invoicing' },
  { key: 'ad_copy_tools', title: 'Ad Copy AI Tools' },
  { key: 'campaign_controls', title: 'Campaign Controls' },
  { key: 'role_permissions', title: 'Role Permissions' },
  { key: 'support_access', title: 'Support Access' },
  { key: 'integrations', title: 'Integrations' },
  { key: 'white_label_domain', title: 'White Label Domain' },
  { key: 'location_management', title: 'Location Management' },
  { key: 'reports_export', title: 'Reports Export' },
];

// Seed default values per role based on the provided matrix
const DEFAULT_ROLES = [
  {
    key: 'admin',
    name: 'Admin',
    permissions: {
      user_management: 'Full control (all accounts)',
      branding_controls: 'Global branding & themes',
      pricing_controls: 'Set global pricing/commissions',
      data_access_scope: 'All accounts & all data',
      audience_creation_tools: 'All filters + advanced tools',
      advanced_data: 'Enable/disable for any tier',
      analytics_reporting: 'All accounts + export',
      billing_invoicing: 'Global billing',
      ad_copy_tools: 'Unlimited',
      campaign_controls: 'Manage all campaigns',
      role_permissions: 'Custom roles & permissions',
      support_access: 'Priority + backend tools',
      integrations: 'Manage global integrations',
      white_label_domain: 'Global',
      location_management: 'All',
      reports_export: 'All formats',
    }
  },
  {
    key: 'agency_whitelabel_reseller',
    name: 'Agency (White Label Reseller)',
    permissions: {
      user_management: 'Create/manage client accounts',
      branding_controls: 'White-label branding for clients',
      pricing_controls: 'Set client pricing',
      data_access_scope: "Only agency's clients",
      audience_creation_tools: 'All filters for clients',
      advanced_data: 'Enable/disable for clients',
      analytics_reporting: 'All clients',
      billing_invoicing: 'Client billing',
      ad_copy_tools: 'Limited per client plan',
      campaign_controls: 'Manage client campaigns',
      role_permissions: 'Assign within agency clients',
      support_access: 'Agency support for clients',
      integrations: 'Enable for clients',
      white_label_domain: "Agency's domain",
      location_management: 'All under agency',
      reports_export: 'CSV/PDF for clients',
    }
  },
  {
    key: 'enterprise_multi_location',
    name: 'Enterprise (Multi-Location)',
    permissions: {
      user_management: 'Create/manage sub-accounts per location',
      branding_controls: 'Not applicable',
      pricing_controls: 'Pricing set by corporate',
      data_access_scope: 'All locations & aggregate reports',
      audience_creation_tools: 'All filters for locations',
      advanced_data: 'Enable/disable per location',
      analytics_reporting: 'Per location + consolidated',
      billing_invoicing: 'Not available',
      ad_copy_tools: 'Limited per corporate plan',
      campaign_controls: 'Manage campaigns across locations',
      role_permissions: 'Assign within corporate & locations',
      support_access: 'Corporate support',
      integrations: 'Enable for locations',
      white_label_domain: 'Corporate branding only',
      location_management: 'All corporate locations',
      reports_export: 'CSV/PDF for locations',
    }
  },
  {
    key: 'small_business_single_location',
    name: 'Small Business (Single Location)',
    permissions: {
      user_management: 'Only own account',
      branding_controls: 'Not applicable',
      pricing_controls: 'Not applicable',
      data_access_scope: 'Own data only',
      audience_creation_tools: 'Standard filters only',
      advanced_data: 'Access only if in plan',
      analytics_reporting: 'Own account',
      billing_invoicing: 'Not available',
      ad_copy_tools: 'Limited per subscription',
      campaign_controls: 'Own campaigns',
      role_permissions: 'Assign within own account',
      support_access: 'Standard support',
      integrations: 'Plan-limited integrations',
      white_label_domain: 'Not available',
      location_management: 'Single location only',
      reports_export: 'CSV/PDF for own account',
    }
  },
];

const RolesPermissions = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [roles, setRoles] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        // Try loading from backend settings (server mounted in backend/server.js as /api/...)
        const resp = await api.get('/settings/roles-permissions');
        if (Array.isArray(resp?.roles)) {
          setRoles(resp.roles);
          return;
        }
      } catch (e) {
        // Seed defaults if endpoint not available yet
        setRoles(DEFAULT_ROLES);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const setPerm = (roleKey, featureKey, value) => {
    setRoles(prev => prev.map(r => r.key === roleKey ? { ...r, permissions: { ...r.permissions, [featureKey]: value } } : r));
  };

  const onSave = async () => {
    try {
      setSaving(true);
      await api.put('/settings/roles-permissions', { roles });
      message.success('Default roles & permissions saved');
    } catch (e) {
      message.error(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Build columns dynamically from FEATURES, each as a Select control per role row
  const columns = [
    { title: 'Role', dataIndex: 'name', key: 'name', width: 220, fixed: 'left' },
    ...FEATURES.map(f => ({
      title: f.title,
      dataIndex: f.key,
      key: f.key,
      width: 260,
      render: (_, record) => {
        // Collect available options for this feature from defaults and current values
        const knownValues = new Set();
        DEFAULT_ROLES.forEach(r => { const v = r.permissions?.[f.key]; if (v) knownValues.add(v); });
        roles.forEach(r => { const v = r.permissions?.[f.key]; if (v) knownValues.add(v); });
        const options = Array.from(knownValues).map(v => ({ label: v, value: v }));
        const value = record.permissions?.[f.key] || '';
        return (
          <Select
            style={{ width: '100%' }}
            options={options}
            value={value}
            onChange={(val) => setPerm(record.key, f.key, val)}
            allowClear={false}
            showSearch
            optionFilterProp="label"
          />
        );
      }
    }))
  ];

  const dataSource = roles.map(r => ({
    key: r.key,
    name: r.name,
    ...Object.fromEntries(FEATURES.map(f => [f.key, r.permissions?.[f.key] || '']))
  }));

  return (
    <div style={{ padding: 24 }}>
      <Breadcrumb items={[
        { title: 'Admin' },
        { title: 'Settings' },
        { title: 'Default Roles & Permissions' },
      ]} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>Default Roles & Permissions</Title>
        <Space>
          <Button type="primary" onClick={onSave} loading={saving}>Save</Button>
        </Space>
      </div>

      <Card>
        <Paragraph type="secondary" style={{ marginBottom: 12 }}>
          Define default permissions for each role. Adjust to your needs and hook up to your backend.
        </Paragraph>
        <Table
          rowKey="key"
          columns={columns}
          dataSource={dataSource}
          loading={loading}
          scroll={{ x: 1800 }}
          pagination={false}
        />
      </Card>
    </div>
  );
};

export default RolesPermissions;
